// Pure arithmetic evaluator for the AmountInput calculator pad.
// Recursive-descent parser — NO eval()/Function() (this is a finance app).
//   expr   := term (('+' | '-') term)*
//   term   := factor (('*' | '/') factor)*
//   factor := '-' factor | '(' expr ')' | number

type Tok =
  | { t: 'num'; v: number }
  | { t: 'op'; v: '+' | '-' | '*' | '/' }
  | { t: 'lp' }
  | { t: 'rp' }

function tokenize(input: string): Tok[] | null {
  const s = input.replace(/,/g, '.').replace(/\s+/g, '')
  if (s === '') return null
  const out: Tok[] = []
  let i = 0
  while (i < s.length) {
    const c = s[i]
    if ((c >= '0' && c <= '9') || c === '.') {
      let j = i
      let dots = 0
      while (j < s.length && ((s[j] >= '0' && s[j] <= '9') || s[j] === '.')) {
        if (s[j] === '.') dots++
        j++
      }
      if (dots > 1) return null
      const v = Number(s.slice(i, j))
      if (!Number.isFinite(v)) return null
      out.push({ t: 'num', v })
      i = j
    } else if (c === '+' || c === '-' || c === '*' || c === '/') {
      out.push({ t: 'op', v: c })
      i++
    } else if (c === '(') {
      out.push({ t: 'lp' })
      i++
    } else if (c === ')') {
      out.push({ t: 'rp' })
      i++
    } else {
      return null
    }
  }
  return out
}

class Parser {
  private pos = 0
  constructor(private readonly toks: Tok[]) {}
  atEnd(): boolean {
    return this.pos >= this.toks.length
  }
  private peek(): Tok | undefined {
    return this.toks[this.pos]
  }
  private next(): Tok | undefined {
    return this.toks[this.pos++]
  }

  parseExpression(): number | null {
    let left = this.parseTerm()
    if (left === null) return null
    for (let tok = this.peek(); tok && tok.t === 'op' && (tok.v === '+' || tok.v === '-'); tok = this.peek()) {
      this.next()
      const right = this.parseTerm()
      if (right === null) return null
      left = tok.v === '+' ? left + right : left - right
    }
    return left
  }

  private parseTerm(): number | null {
    let left = this.parseFactor()
    if (left === null) return null
    for (let tok = this.peek(); tok && tok.t === 'op' && (tok.v === '*' || tok.v === '/'); tok = this.peek()) {
      this.next()
      const right = this.parseFactor()
      if (right === null) return null
      if (tok.v === '/') {
        if (right === 0) return null
        left = left / right
      } else {
        left = left * right
      }
    }
    return left
  }

  private parseFactor(): number | null {
    const tok = this.peek()
    if (!tok) return null
    if (tok.t === 'op' && tok.v === '-') {
      this.next()
      const f = this.parseFactor()
      return f === null ? null : -f
    }
    if (tok.t === 'lp') {
      this.next()
      const e = this.parseExpression()
      if (e === null) return null
      const close = this.peek()
      if (!close || close.t !== 'rp') return null
      this.next()
      return e
    }
    if (tok.t === 'num') {
      this.next()
      return tok.v
    }
    return null
  }
}

export function evalExpression(input: string): number | null {
  const toks = tokenize(input)
  if (toks === null) return null
  const parser = new Parser(toks)
  const result = parser.parseExpression()
  if (result === null || !parser.atEnd() || !Number.isFinite(result)) return null
  return result
}
